import { useRef, useEffect, useState } from 'react';
import { Spin } from 'antd';
import { sanitizeHtmlForEmail } from '../../utils/sanitizeHtml';

const SafeContainerTinyMCE = ({
  content,
  handleChange,
  readOnly = false,
  placeholder,
  height = 500,
  language = 'es',
}) => {
  const editorRef = useRef(null);
  const [Editor, setEditor] = useState(null);
  const editorKey = 0;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('@tinymce/tinymce-react').then((module) => {
        setEditor(() => module.Editor);
      });
    }

    return () => {
      if (editorRef.current) {
        editorRef.current = null;
      }
    };
  }, []);

  if (typeof window === 'undefined' || !Editor) {
    return <Spin tip="Cargando editor..." />;
  }

  const languageMap = {
    es: 'es',
    en: 'en',
    pt: 'pt_BR',
  };

  const editorConfig = {
    height,
    menubar: false,
    license_key: 'gpl',
    branding: false,
    promotion: false,
    language: languageMap[language] || 'es',
    language_url:
      languageMap[language] !== 'en'
        ? `https://cdn.jsdelivr.net/npm/tinymce-i18n@latest/langs6/${languageMap[language]}.min.js`
        : undefined,
    plugins: [
      'advlist',
      'autolink',
      'lists',
      'link',
      'image',
      'charmap',
      'searchreplace',
      'visualblocks',
      'code',
      'fullscreen',
      'insertdatetime',
      'media',
      'table',
      'preview',
      'help',
      'wordcount',
      'codesample',
      'emoticons',
      'directionality',
      'nonbreaking',
      'anchor',
      'quickbars',
    ],
    toolbar: readOnly
      ? false
      : 'preview | undo redo | formatselect | ' +
        'bold italic underline strikethrough | forecolor backcolor | ' +
        'alignleft aligncenter alignright alignjustify | ' +
        'bullist numlist outdent indent | ' +
        'link image table | ' +
        'removeformat code fullscreen',

    valid_elements: '*[*]',
    extended_valid_elements: '*[*]',
    valid_children: '+body[style],+div[style]',
    verify_html: false,
    cleanup: false,
    forced_root_block: 'p',
    force_br_newlines: false,
    force_p_newlines: true,

    inline_styles: true,
    keep_styles: true,

    formats: {
      alignleft: {
        selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li',
        styles: { textAlign: 'left' },
      },
      aligncenter: {
        selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li',
        styles: { textAlign: 'center' },
      },
      alignright: {
        selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li',
        styles: { textAlign: 'right' },
      },
      alignjustify: {
        selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li',
        styles: { textAlign: 'justify' },
      },
    },

    content_style: `
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
        font-size: 14px;
        line-height: 1.6;
        margin: 10px;
      }
      table {
        border-collapse: collapse;
        width: 100%;
      }
      img {
        max-width: 100%;
        height: auto;
      }
      [style*="text-align: justify"],
      [style*="text-align:justify"] {
        text-align: justify !important;
        text-justify: inter-word;
      }
      .ql-align-center {
        text-align: center !important;
      }
      .ql-align-right {
        text-align: right !important;
      }
      .ql-align-left {
        text-align: left !important;
      }
      .ql-align-justify {
        text-align: justify !important;
        text-justify: inter-word;
      }
    `,

    entity_encoding: 'raw',
    remove_trailing_brs: false,

    table_default_attributes: {
      border: '0',
      cellpadding: '0',
      cellspacing: '0',
      width: '100%',
    },
    table_class_list: [
      { title: 'None', value: '' },
      { title: 'Email Table', value: 'email-table' },
    ],

    paste_data_images: true,
    paste_webkit_styles: 'all',
    paste_merge_formats: false,

    automatic_uploads: false,
    images_upload_handler: null,

    browser_spellcheck: true,
    contextmenu: false,

    convert_urls: false,
    relative_urls: false,
    remove_script_host: false,

    doctype:
      '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">',
    element_format: 'html',
    schema: 'html4',

    placeholder: placeholder || 'Escribe aquí el contenido del email...',

    readonly: readOnly,

    setup: (editor) => {
      let changeTimeout = null;

      editor.on('init', () => {
        editorRef.current = editor;

        if (content && editor.getContent() !== content) {
          let cleanedContent = sanitizeHtmlForEmail(content);

          const parser = new DOMParser();
          const doc = parser.parseFromString(cleanedContent, 'text/html');
          const elementsWithQuillAlign = doc.querySelectorAll('[class*="ql-align-"]');

          elementsWithQuillAlign.forEach((el) => {
            const classes = Array.from(el.classList);
            classes.forEach((className) => {
              if (className.startsWith('ql-align-')) {
                const alignment = className.replace('ql-align-', '');
                el.style.textAlign = alignment;
                el.classList.remove(className);
              }
            });
          });

          cleanedContent = doc.body.innerHTML;
          editor.setContent(cleanedContent);
        }
      });

      editor.on('NodeChange Change', () => {
        if (handleChange) {
          if (changeTimeout) {
            clearTimeout(changeTimeout);
          }
          changeTimeout = setTimeout(() => {
            handleChange(editor.getContent());
          }, 300);
        }
      });

      editor.on('beforeSetContent', (e) => {
        if (e.initial) {
          e.format = 'raw';
        }
      });

      editor.on('paste', (e) => {
        e.preventDefault();
        const clipboardData = e.clipboardData || window.clipboardData;

        const processHTMLPaste = (html) => {
          const allowedStylesSet = new Set([
            'color',
            'background-color',
            'text-align',
            'font-weight',
            'font-style',
            'text-decoration',
          ]);

          return html.replace(/\sstyle="([^"]*)"/g, (match, styleContent) => {
            const cleanedStyles = styleContent
              .split(';')
              .map((rule) => rule.trim())
              .filter((rule) => {
                const prop = rule.split(':')[0]?.trim();
                return prop && allowedStylesSet.has(prop);
              })
              .join('; ');

            return cleanedStyles ? ` style="${cleanedStyles}"` : '';
          });
        };

        const processPlainTextPaste = (text) => {
          const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
          const result = [];
          let currentList = null;
          let currentListType = null;

          const escapeHtml = (str) =>
            str.replace(
              /[&<>"']/g,
              (m) =>
                ({
                  '&': '&amp;',
                  '<': '&lt;',
                  '>': '&gt;',
                  '"': '&quot;',
                  "'": '&#39;',
                }[m])
            );

          const linkify = (str) =>
            str
              .replace(
                /(https?:\/\/[^\s]+)/g,
                '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
              )
              .replace(
                /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/g,
                '<a href="mailto:$1">$1</a>'
              );

          lines.forEach((line) => {
            const trimmed = line.trim();
            const bulletMatch = trimmed.match(/^[-*•]\s+(.+)$/);
            const numberedMatch = trimmed.match(/^\d+[.)]\s+(.+)$/);

            if (bulletMatch || numberedMatch) {
              const listType = bulletMatch ? 'ul' : 'ol';
              const content = bulletMatch ? bulletMatch[1] : numberedMatch[1];

              if (currentListType !== listType) {
                if (currentList) {
                  result.push(`<${currentListType}>${currentList.join('')}</${currentListType}>`);
                  currentList = null;
                }
                currentList = [];
                currentListType = listType;
              }

              currentList.push(`<li>${linkify(escapeHtml(content))}</li>`);
            } else {
              if (currentList) {
                result.push(`<${currentListType}>${currentList.join('')}</${currentListType}>`);
                currentList = null;
                currentListType = null;
              }

              if (trimmed === '') {
                result.push('<p><br></p>');
              } else {
                result.push(`<p>${linkify(escapeHtml(trimmed))}</p>`);
              }
            }
          });

          if (currentList) {
            result.push(`<${currentListType}>${currentList.join('')}</${currentListType}>`);
          }

          return result.join('');
        };

        const htmlData = clipboardData.getData('text/html');
        const plainText = clipboardData.getData('text/plain');

        let processedData;

        if (htmlData) {
          processedData = processHTMLPaste(htmlData);
        } else if (plainText) {
          processedData = processPlainTextPaste(plainText);
        }

        if (processedData) {
          const sanitizedData = sanitizeHtmlForEmail(processedData);
          editor.insertContent(sanitizedData);
        }
      });
    },

    init_instance_callback: () => {
      // TinyMCE Email Editor initialized
    },
  };

  return (
    <>
      <style>{`
        .tinymce-container {
          width: 100%;
        }
        .tinymce-container .tox-tinymce {
          border: 1px solid #d9d9d9;
          border-radius: 2px;
        }
        .tinymce-container .tox-toolbar__primary {
          background: #fafafa;
          border-bottom: 1px solid #d9d9d9;
        }
        .tinymce-container .tox-edit-area {
          background: #fff;
        }
      `}</style>
      <div className="tinymce-container">
        <Editor
          key={editorKey}
          tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@6/tinymce.min.js"
          value={content}
          init={editorConfig}
          onEditorChange={handleChange}
          disabled={readOnly}
        />
      </div>
    </>
  );
};

export default SafeContainerTinyMCE;
